// RemovableOperationLog.ts
import BaseEntity from '../base/BaseEntity';
import Filter from '../base/filter/Filter';
import InputFilter from '../base/filter/InputFilter';
import SortFilter from '../base/filter/SortFilter';
import SelectionFilter from '../base/filter/SelectionFilter';
import Lang from '../global/Lang';
import SortDirection from '../base/SortDirection';
import DateUtil from '../../util/DateUtil';
import EnvUtil from '../../util/EnvUtil';
import SafeUtil from '../../util/SafeUtil';

export default class RemovableOperationLog extends BaseEntity {
  uuid: string = '';
  userUuid: string = '';
  username: string = '';
  deviceId: string = '';
  operation: string = '';
  filePath: string = '';
  success: boolean = true;
  errorMsg: string = '';
  clientIp: string = '';
  // 修正：继承自BaseEntity的createTime是Date类型
  // 这里我们添加一个新的字段来存储字符串格式的时间
  createTimeStr: string = '';
  
  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  assign(obj: any) {
    super.assign(obj);
    
    // 将字符串时间转换为Date对象
    if (obj.createTime) {
      this.createTime = new Date(obj.createTime);
      this.createTimeStr = obj.createTime;
    }
    
    // 处理其他字段
    if (obj.userUuid) this.userUuid = obj.userUuid;
    if (obj.username) this.username = obj.username;
    if (obj.deviceId) this.deviceId = obj.deviceId;
    if (obj.operation) this.operation = obj.operation;
    if (obj.filePath) this.filePath = obj.filePath;
    if (obj.success !== undefined) this.success = obj.success;
    if (obj.errorMsg) this.errorMsg = obj.errorMsg;
    if (obj.clientIp) this.clientIp = obj.clientIp;
  }

  getTAG(): string {
    return 'removable-operation-log';
  }

  getForm(): any {
    return {
      userUuid: this.userUuid,
      username: this.username,
      deviceId: this.deviceId,
      operation: this.operation,
      filePath: this.filePath,
      success: this.success,
      errorMsg: this.errorMsg,
      clientIp: this.clientIp,
      createTime: this.createTimeStr,
    };
  }

  // 获取操作类型标签
  getOperationTag(): { color: string; text: string } {
    const operations: Record<string, { color: string; text: string }> = {
      'LIST_DEVICES': { color: 'blue', text: Lang.t('removable.opListDevices') },
      'BROWSE': { color: 'green', text: Lang.t('removable.opBrowse') },
      'DOWNLOAD': { color: 'cyan', text: Lang.t('removable.opDownload') },
      'PREVIEW': { color: 'purple', text: Lang.t('removable.opPreview') },
      'RENAME': { color: 'orange', text: Lang.t('removable.opRename') },
      'UPLOAD': { color: 'magenta', text: Lang.t('removable.opUpload') },
      'UPLOAD_FOLDER': { color: 'magenta', text: Lang.t('removable.uploadFolder') },
      'DELETE': { color: 'red', text: Lang.t('removable.opDelete') },
      'MKDIR': { color: 'red', text: Lang.t('log.createDirectory') },
    };
    
    return operations[this.operation] || { color: 'default', text: this.operation };
  }
  
  // 获取操作类型文本
  getOperationText(): string {
    const operations: Record<string, string> = {
      'LIST_DEVICES': Lang.t('removable.opListDevices'),
      'BROWSE': Lang.t('removable.opBrowse'),
      'DOWNLOAD': Lang.t('removable.opDownload'),
      'PREVIEW': Lang.t('removable.opPreview'),
      'RENAME': Lang.t('removable.opRename'),
      'UPLOAD': Lang.t('removable.opUpload'),
      'UPLOAD_FOLDER':  Lang.t('removable.uploadFolder'),
      'DELETE': Lang.t('removable.opDelete'),
      'MKDIR':Lang.t('log.createDirectory'),
    };
    
    return operations[this.operation] || this.operation;
  }
  
  // 获取成功状态标签
  getSuccessTag(): { color: string; text: string } {
    return this.success 
      ? { color: 'success', text: Lang.t('space.success') }
      : { color: 'error', text: Lang.t('space.failed') };
  }
  
  // 格式化时间 - 使用DateUtil
  getFormattedTime(): string {
    return DateUtil.simpleDateTime(this.createTime);
  }
  
  // 获取操作描述
  getOperationDescription(): string {
    const operations: Record<string, string> = {
      'LIST_DEVICES': Lang.t('removable.opDescListDevices'),
      'BROWSE': Lang.t('removable.opDescBrowse'),
      'DOWNLOAD': Lang.t('removable.opDescDownload'),
      'PREVIEW': Lang.t('removable.opDescPreview'),
      'RENAME': Lang.t('removable.opDescRename'),
      'UPLOAD': Lang.t('removable.opDescUpload'),
      'UPLOAD_FOLDER': Lang.t('removable.opDescUploadFloader'),
      'DELETE': Lang.t('removable.opDescDelete'),
      'MKDIR':Lang.t('log.createDirectory'),
    };
    
    return operations[this.operation] || this.operation;
  }

  getFilters(): Filter[] {
    const operationOptions = [
      { value: 'LIST_DEVICES', name: Lang.t('removable.opListDevices') },
      { value: 'BROWSE', name: Lang.t('removable.opBrowse') },
      { value: 'DOWNLOAD', name: Lang.t('removable.opDownload') },
      { value: 'PREVIEW', name: Lang.t('removable.opPreview') },
      { value: 'RENAME', name: Lang.t('removable.opRename') },
      { value: 'UPLOAD', name: Lang.t('removable.opUpload') },
      { value: 'DELETE', name: Lang.t('removable.opDelete') },
      { value: 'MKDIR', name: Lang.t('log.createDirectory') },
    ];

    const successOptions = [
      { value: 'true', name: Lang.t('common.success') },
      { value: 'false', name: Lang.t('space.failed') },
    ];

    return [
      ...super.getFilters(),
      new InputFilter(Lang.t('common.search'), 'keyword', Lang.t('removable.searchLogs')),
      new InputFilter(Lang.t('removable.user'), 'username'),
      new InputFilter(Lang.t('removable.device'), 'deviceId'),
      new SelectionFilter(Lang.t('removable.operation'), 'operation', operationOptions),
      new SelectionFilter(Lang.t('removable.status'), 'success', successOptions),
      new InputFilter(Lang.t('removable.startTime'), 'startTime'),
      new InputFilter(Lang.t('removable.endTime'), 'endTime'),
      new SortFilter(Lang.t('common.createTime'), 'orderCreateTime'),
    ];
  }

  getUrlPrefix(): string {
    return '/api/removable/';
  }

  getUrlList(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}logs`;
  }

  // 获取详情
  httpDetail(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    let that = this;
    if (!this.uuid) {
      this.errorMessage = 'uuid未指定，无法获取到详情！';
      this.defaultErrorHandler(this.errorMessage, errorCallback);
      return;
    }

    this.detailLoading = true;

    this.httpGet(
      this.getUrlDetail(),
      {
        uuid: this.uuid,
      },
      function (response: any) {
        that.detailLoading = false;
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.detailLoading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      },
      finallyCallback
    );
  }

  getUrlDetail(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}detail`;
  }
}